/* The time zone list in Settings (owner request 2026-10-13): each line is a clean place name, the abbreviation people
   really use (where there is one) and the UTC offset today, e.g. "Chicago (Central) — CDT · UTC−5",
   "Manila (Philippines) — PHT · UTC+8", "Buenos Aires (Argentina) — UTC−3". Searchable by place, country, abbreviation,
   offset ("+8", "UTC+8", "-5") and the everyday US names ("central"). Order: the common zones first (see zone.ts), then
   every other zone by its UTC offset today (west to east) and then by name.
   Pure apart from Intl (tests/app/zonenames.test.ts). ~420 zones: one Intl formatter per zone, built once and kept. */
import {canonicalZone, countryOf, countryName, placeLabel, placeName, usZone, zoneTime} from './zonenames.ts';

/** the locale that makes the browser give a zone its real abbreviation (en-US: CDT, en-AU: AEST, en-GB: CEST, en-IN: IST) */
const EUROPE = new Set('AD AL AT AX BA BE BG BY CH CY CZ DE DK EE ES FI FO FR GG GI GR HR HU IE IM IS IT JE LI LT LU LV MC MD ME MK MT NL NO PL PT RO RS SE SI SK SM UA VA XK'.split(' '));
function localeFor(cc: string): string {
  if (!cc || cc === 'US') return 'en-US';
  if (cc === 'GB' || EUROPE.has(cc) && cc !== 'IE') return 'en-GB';
  return 'en-' + cc;
}
/** abbreviations in common use that browsers often do not give (standard time, daylight time) */
const CET: [string, string] = ['CET', 'CEST'], EET: [string, string] = ['EET', 'EEST'], WET: [string, string] = ['WET', 'WEST'];
const BY_COUNTRY: Record<string, [string, string?]> = {
  PH: ['PHT'], JP: ['JST'], KR: ['KST'], HK: ['HKT'], SG: ['SGT'], CN: ['CST'], TW: ['CST'], IN: ['IST'], PK: ['PKT'], TH: ['ICT'], VN: ['ICT'],
  KH: ['ICT'], LA: ['ICT'], MY: ['MYT'], AE: ['GST'], OM: ['GST'], NP: ['NPT'], TR: ['TRT'], IL: ['IST', 'IDT'], NZ: ['NZST', 'NZDT'],
  KE: ['EAT'], TZ: ['EAT'], UG: ['EAT'], ET: ['EAT'], NG: ['WAT'], ZA: ['SAST'], EG: EET,
  AT: CET, BE: CET, CH: CET, CZ: CET, DE: CET, DK: CET, ES: CET, FR: CET, HR: CET, HU: CET, IT: CET, LU: CET, NL: CET, NO: CET, PL: CET, SE: CET,
  SI: CET, SK: CET, RS: CET, BA: CET, ME: CET, MK: CET, AL: CET, MT: CET, MC: CET, AD: CET, LI: CET, SM: CET, VA: CET, GI: CET,
  BG: EET, CY: EET, EE: EET, FI: EET, GR: EET, LT: EET, LV: EET, MD: EET, RO: EET, UA: EET, PT: WET, FO: WET,
};
const BY_ZONE: Record<string, [string, string?]> = {
  'Europe/Moscow': ['MSK'], 'Asia/Jakarta': ['WIB'], 'Asia/Pontianak': ['WIB'], 'Asia/Makassar': ['WITA'], 'Asia/Jayapura': ['WIT'],
  'Atlantic/Canary': WET, 'Atlantic/Madeira': WET, 'Africa/Ceuta': CET, 'America/Sao_Paulo': ['BRT'],
};
/** "UTC−5", "UTC+8", "UTC+5:30", "UTC+0" (the real minus sign) */
export function utcText(min: number): string {
  const a = Math.abs(min), h = Math.floor(a / 60), m = a % 60;
  return 'UTC' + (min < 0 ? '−' : '+') + h + (m ? ':' + String(m).padStart(2, '0') : '');
}

const fmts = new Map<string, Intl.DateTimeFormat | null>();
function fmt(z: string, locale: string): Intl.DateTimeFormat | null {
  const k = locale + '|' + z;
  let f = fmts.get(k);
  if (f === undefined) {
    try { f = new Intl.DateTimeFormat(locale, {timeZone: z, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', timeZoneName: 'short'}); }
    catch { f = null; }
    fmts.set(k, f);
  }
  return f;
}
/** offset (minutes) and the browser's short name at an instant, from one formatter */
function read(f: Intl.DateTimeFormat, ms: number): {offset: number; name: string} {
  const p: Record<string, string> = {};
  for (const x of f.formatToParts(new Date(ms))) p[x.type] = x.value;
  const hour = p.hour === '24' ? 0 : +p.hour;
  const wall = Date.UTC(+p.year, +p.month - 1, +p.day, hour, +p.minute, +p.second);
  return {offset: Math.round((wall - Math.floor(ms / 1000) * 1000) / 60000), name: p.timeZoneName || ''};
}
const generic = (n: string) => !n || /^(GMT|UTC)[+\-−]/.test(n);

export interface ZoneInfo { zone: string; offset: number; abbr: string; utc: string; label: string; line: string }
/** a zone today: offset, abbreviation ('' when none is in common use), its list line */
export function zoneInfo(z: string, at = Date.now()): ZoneInfo {
  const cc = countryOf(z), c = canonicalZone(z), f = fmt(z, localeFor(cc)) || fmt(z, 'en-US');
  let offset = 0, abbr = '';
  if (f) {
    const r = read(f, at); offset = r.offset; abbr = generic(r.name) ? '' : r.name;
    if (!abbr && c.startsWith('America/') && cc !== 'AR') { const u = fmt(z, 'en-US'); if (u) { const n = read(u, at).name; if (!generic(n)) abbr = n; } }
    const cur = !abbr && (BY_ZONE[c] || BY_COUNTRY[cc]);
    if (cur) {
      const y = new Date(at).getUTCFullYear(), std = Math.min(read(f, Date.UTC(y, 0, 1)).offset, read(f, Date.UTC(y, 6, 1)).offset);
      abbr = offset > std && cur[1] ? cur[1] : cur[0];
    }
    if (abbr === 'UTC') abbr = '';   // "UTC — UTC+0", not "UTC — UTC · UTC+0"
  }
  const utc = utcText(offset), label = placeLabel(z);
  return {zone: z, offset, abbr, utc, label, line: label + ' — ' + (abbr ? abbr + ' · ' : '') + utc};
}
/** "PHT · UTC+8", "UTC−3": the zone's code and offset today (Settings' summary) */
export const zoneCode = (z: string, at = Date.now()) => { const i = zoneInfo(z, at); return (i.abbr ? i.abbr + ' · ' : '') + i.utc; };

/* ---------- the searchable list ---------- */
export interface ZoneRow extends ZoneInfo { hay: string }
const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[−–]/g, '-').replace(/[_/]+/g, ' ').replace(/\s+/g, ' ').trim();
/** one row per zone (built once per hour: offsets and abbreviations change on daylight-saving days) */
let cache: {key: string; rows: ZoneRow[]} | null = null;
export function zoneRows(zones: string[], at = Date.now()): ZoneRow[] {
  const key = Math.floor(at / 3600000) + '|' + zones.length + '|' + zones[0] + '|' + zones[zones.length - 1];
  if (cache && cache.key === key) return cache.rows;
  const rows = zones.map(z => {
    const i = zoneInfo(z, at), us = usZone(z);
    return {...i, hay: norm([i.label, z, canonicalZone(z), placeName(z), countryName(countryOf(z)), zoneTime(z), us, us ? us + ' time' : '', i.abbr].join(' | '))};
  });
  cache = {key, rows};
  return rows;
}
/** a search for an offset: "+8", "UTC+8", "gmt-5", "-5", "+5:30", "utc 0" -> minutes; null for anything else */
export function offsetQuery(q: string): number | null {
  const m = /^(?:utc|gmt)?\s*([+-])?\s*(\d{1,2})(?::?(\d{2}))?$/.exec(norm(q));
  if (!m || (!m[1] && !/^(utc|gmt)/.test(norm(q)))) return null;   // a bare "8" is not an offset (it could be part of a name)
  const v = +m[2] * 60 + (m[3] ? +m[3] : 0);
  return v > 14 * 60 ? null : (m[1] === '-' ? -v : v);
}
/** the rows a search keeps: an offset search matches that offset exactly; any other words must all appear */
export function searchZones(rows: ZoneRow[], q: string): ZoneRow[] {
  const s = norm(q); if (!s) return rows;
  const off = offsetQuery(s);
  if (off !== null) return rows.filter(r => r.offset === off);
  const words = s.split(' ');
  return rows.filter(r => words.every(w => r.hay.includes(w)));
}
/** every other zone by its UTC offset today (west to east), then by its line */
export const byOffset = (a: ZoneRow, b: ZoneRow) => a.offset - b.offset || a.line.localeCompare(b.line);

/** the groups of the Settings list.
    No search: "Common" (the common zones), "This device's zone", "All time zones (by UTC offset)".
    A search: ONE list, "Matching time zones" — the common zones that match first (so "+8" shows Manila on top), then the
    others by offset and name; the current zone, when it does not match, comes FIRST as "Your zone now" (a list's
    selected line is where a phone or Windows opens it: at the end, it scrolled the matches above out of view — the
    "+8 without Manila" report of 2026-10-13). */
export interface ZoneGroup { label: string; rows: ZoneRow[]; device?: boolean }
export function zoneGroups(rows: ZoneRow[], q: string, cur: string, dev: string, common: string[]): ZoneGroup[] {
  const row = new Map(rows.map(r => [r.zone, r])), hit = searchZones(rows, q), keep = new Set(hit.map(r => r.zone));
  const commonRows = (only: (z: string) => boolean) => common.filter(only).map(z => row.get(z)).filter((r): r is ZoneRow => !!r);
  const rest = hit.filter(r => !common.includes(r.zone)).sort(byOffset);
  const out: ZoneGroup[] = [];
  if (q.trim()) {
    const curRow = row.get(cur);
    if (curRow && !keep.has(cur)) out.push({label: 'Your zone now', rows: [curRow]});
    const all = [...commonRows(z => keep.has(z)), ...rest];
    if (all.length) out.push({label: 'Matching time zones', rows: all});
    return out;
  }
  const c = commonRows(() => true); if (c.length) out.push({label: 'Common', rows: c});
  const devRow = dev && !common.includes(dev) ? row.get(dev) : undefined;
  if (devRow) out.push({label: "This device's zone", rows: [devRow], device: true});
  if (rest.length) out.push({label: 'All time zones (by UTC offset)', rows: rest});
  return out;
}
